import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { getPricingSummary, getLandingContent, fmtMoney } from "@/lib/api";
import {
  Sparkles,
  Clock,
  ShieldCheck,
  TrendingUp,
  Users,
  ClipboardList,
  Gauge,
  ArrowRight,
  Check,
  Target,
  Briefcase,
  HeartPulse,
  Headset,
  Wrench,
} from "lucide-react";

// Icon per public pricing bucket (keyed by backend bucket key).
const BUCKET_ICONS = {
  administrative: Briefcase,
  healthcare: HeartPulse,
  sales_growth: Headset,
  technical_professional: Wrench,
};

const steps = [
  {
    icon: ClipboardList,
    title: "Answer a few questions",
    body: "Tell us about your industry, workload, and where your team feels the strain.",
  },
  {
    icon: Gauge,
    title: "Get an instant preview",
    body: "See your recommended team structure before sharing any details.",
  },
  {
    icon: Users,
    title: "Unlock your blueprint",
    body: "Receive a personalized workforce plan with roles, responsibilities, and an implementation roadmap.",
  },
];

const stats = [
  { icon: Clock, label: "~3 minutes", sub: "to complete" },
  { icon: ShieldCheck, label: "No obligation", sub: "free assessment" },
  { icon: TrendingUp, label: "Dedicated", sub: "managed teams" },
];

// Built-in fallback hero copy — mirrors the backend defaults so the page still
// renders instantly if the editable content hasn't loaded yet.
const HERO_FALLBACK = {
  badge: "TeamBuilder AI\u2122 \u2014 Workforce Blueprint Generator",
  title: "Design your optimized global talent team in minutes.",
  highlight: "global talent team",
  subtitle:
    "Answer a short, consultative assessment and instantly receive a personalized team blueprint \u2014 recommended roles, responsibilities, and an implementation roadmap.",
  cta: "Start Your Assessment",
};

// Render the headline, wrapping the (optional) highlighted phrase in the brand
// accent color. Falls back to plain text if the phrase isn't found.
const renderTitle = (title, highlight) => {
  const t = title || "";
  const h = (highlight || "").trim();
  const idx = h ? t.indexOf(h) : -1;
  if (idx === -1) return t;
  return (
    <>
      {t.slice(0, idx)}
      <span className="text-[#1E6BFF]">{h}</span>
      {t.slice(idx + h.length)}
    </>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [hero, setHero] = useState(HERO_FALLBACK);

  useEffect(() => {
    getPricingSummary()
      .then((d) => setPricing(d && d.show_pricing ? d : null))
      .catch(() => setPricing(null));
    getLandingContent()
      .then((d) => { if (d && d.hero) setHero({ ...HERO_FALLBACK, ...d.hero }); })
      .catch(() => { /* keep fallback copy */ });
  }, []);

  return (
    <div className="min-h-screen rd-bg text-white">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden rd-noise">
        <div className="absolute inset-0 rd-glow pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-[#66B2FF] mb-6"
              data-testid="landing-hero-badge"
            >
              <Sparkles size={14} /> {hero.badge}
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.05 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
              data-testid="landing-hero-title"
            >
              {renderTitle(hero.title, hero.highlight)}
            </motion.h1>
            <p className="text-white/70 text-base sm:text-lg mt-6 max-w-xl leading-relaxed" data-testid="landing-hero-subtitle">
              {hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Button
                data-testid="landing-start-assessment-button"
                onClick={() => navigate("/assessment")}
                className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-xl h-12 px-6 text-base shadow-[0_10px_30px_rgba(30,107,255,0.25)]"
              >
                {hero.cta} <ArrowRight size={18} className="ml-1" />
              </Button>
              <Button
                variant="secondary"
                onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-[#121A2E] hover:border-white/25 border border-white/10 text-white/90 rounded-xl h-12 px-6 text-base"
                data-testid="landing-how-it-works-button"
              >
                How it works
              </Button>
            </div>
            <div className="flex flex-wrap gap-6 mt-10">
              {stats.map((s) => (
                <div key={s.label} className="flex items-center gap-2.5">
                  <span className="rounded-lg bg-white/5 p-2 text-[#66B2FF]">
                    <s.icon size={18} />
                  </span>
                  <div>
                    <div className="text-white font-semibold text-sm">{s.label}</div>
                    <div className="text-white/50 text-xs">{s.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative"
          >
            <div
              className="rounded-2xl border border-white/10 p-6 sm:p-8"
              style={{ backgroundColor: "#0F1424" }}
              data-testid="sample-blueprint-card"
            >
              <div className="flex items-center justify-between mb-5">
                <span className="text-xs uppercase tracking-widest text-[#66B2FF] font-semibold">
                  Sample Workforce Blueprint
                </span>
                <span className="text-xs text-white/40">Healthcare Operations</span>
              </div>
              {[
                { r: "Insurance Verification Specialists", q: 2 },
                { r: "Authorization Specialist", q: 1 },
                { r: "Medical Administrative Assistant", q: 1 },
              ].map((x) => (
                <div
                  key={x.r}
                  className="flex items-center gap-3 py-3 border-b border-white/10 last:border-0"
                >
                  <span className="h-7 w-7 shrink-0 rounded-lg bg-[#2EE59D]/15 text-[#2EE59D] flex items-center justify-center">
                    <Check size={15} />
                  </span>
                  <div>
                    <div className="text-white text-sm font-medium">{x.r}</div>
                    <div className="text-white/45 text-xs">
                      {x.q} Dedicated Team Member{x.q > 1 ? "s" : ""}
                    </div>
                  </div>
                </div>
              ))}

              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-[#1E6BFF]/10 border border-[#1E6BFF]/25 p-4">
                  <div className="flex items-center gap-2 text-white/60 text-xs mb-1">
                    <Users size={13} /> Estimated Team Size
                  </div>
                  <div className="text-white font-bold text-lg">4 Dedicated Professionals</div>
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                  <div className="flex items-center gap-2 text-white/60 text-xs mb-1">
                    <Clock size={13} /> Estimated Implementation
                  </div>
                  <div className="text-white font-bold text-lg">2&ndash;4 Weeks</div>
                </div>
              </div>

              <div className="mt-5">
                <div className="flex items-center gap-2 text-white/70 text-xs font-semibold mb-2">
                  <Target size={13} className="text-[#66B2FF]" /> Primary Objectives
                </div>
                <ul className="space-y-1.5">
                  {[
                    "Reduce administrative workload",
                    "Improve patient scheduling",
                    "Accelerate insurance processing",
                    "Increase operational efficiency",
                  ].map((o) => (
                    <li key={o} className="text-white/60 text-sm flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#66B2FF]" />
                      {o}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Typical investments (dynamic, from Role Intelligence) */}
      {pricing && pricing.categories && pricing.categories.length > 0 && (
        <section id="pricing" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20" data-testid="landing-pricing">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-[#66B2FF] mb-4">
              <TrendingUp size={14} /> Typical Investment
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Dedicated support teams starting around{" "}
              <span className="text-[#1E6BFF]">{fmtMoney(pricing.starting_from)}/month</span>
            </h2>
            <p className="text-white/60 mt-3">
              Every engagement is scoped to your business. Here are a few broad examples to
              give you a sense of typical monthly investment before you begin.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {pricing.categories.map((c) => {
              const Icon = BUCKET_ICONS[c.key] || Users;
              return (
                <div
                  key={c.key}
                  className="rounded-xl border border-white/10 p-6 flex flex-col"
                  style={{ backgroundColor: "#0F1424" }}
                  data-testid={`pricing-card-${c.key}`}
                >
                  <span className="h-10 w-10 rounded-xl bg-[#1E6BFF]/15 text-[#66B2FF] flex items-center justify-center mb-4">
                    <Icon size={20} />
                  </span>
                  <h3 className="text-white font-semibold text-base leading-snug">{c.label}</h3>
                  <p className="text-white/55 text-sm mt-2 leading-relaxed flex-1">{c.blurb}</p>
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <div className="text-white/45 text-xs">Starting around</div>
                    <div className="text-white font-bold text-xl tabular-nums mt-0.5">
                      {fmtMoney(c.starting_monthly)}
                      <span className="text-white/50 text-sm font-medium">/month</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-white/45 text-xs mt-6 leading-relaxed max-w-3xl" data-testid="landing-pricing-note">
            {pricing.starting_note} {pricing.disclaimer}
          </p>

          <div className="mt-8">
            <Button
              data-testid="landing-pricing-cta-button"
              onClick={() => navigate("/assessment")}
              className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-xl h-12 px-6 text-base shadow-[0_10px_30px_rgba(30,107,255,0.25)]"
            >
              Build Your Workforce Blueprint <ArrowRight size={18} className="ml-1" />
            </Button>
          </div>
        </section>
      )}

      {/* How it works */}
      <section id="how-it-works" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">How it works</h2>
          <p className="text-white/60 mt-3">
            A guided, consultative experience &mdash; not a contact form. Get value
            first, then decide.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {steps.map((s, i) => (
            <div
              key={s.title}
              className="rounded-xl border border-white/10 p-6"
              style={{ backgroundColor: "#0F1424" }}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="h-10 w-10 rounded-xl bg-[#1E6BFF] text-white flex items-center justify-center">
                  <s.icon size={20} />
                </span>
                <span className="text-white/40 text-sm font-semibold">0{i + 1}</span>
              </div>
              <h3 className="text-white font-semibold text-lg">{s.title}</h3>
              <p className="text-white/60 text-sm mt-2 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-12">
          <Button
            data-testid="landing-cta-bottom-button"
            onClick={() => navigate("/assessment")}
            className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-xl h-12 px-6 text-base"
          >
            Build my team blueprint <ArrowRight size={18} className="ml-1" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
